import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import KanjiCard from "@/components/KanjiCard";
import BuildTree from "@/components/BuildTree";
import CompoundList from "@/components/CompoundList";
import PhoneticTree from "@/components/PhoneticTree";
import { TEST_IDS } from "@/constants/testIds";

const TABS = [
  { id: "card",  label: "Card",  testId: "tab-card" },
  { id: "build", label: "Build", testId: "tab-build" },
];
// Build-tab inner views. The Build tab is now a toggle between the
// VISUAL decomposition (the BuildTree — radicals/components/strokes)
// and the PHONETIC tree (sound-family relations). The two share enough
// surface area conceptually (both are "how this kanji is built") that
// it makes more sense as a sub-toggle than as a separate top-level tab.
const BUILD_VIEWS = [
  { id: "visual",   label: "Visual",   testId: "build-view-visual" },
  { id: "phonetic", label: "Phonetic", testId: "build-view-phonetic" },
];

export default function KanjiFocus() {
  const { ch } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?tab= deep-link support. Legacy deep-links use `?tab=phonetic`
  // (from the Atlas, popovers, etc) — we accept that as a shorthand
  // meaning "Build tab, phonetic view" so old URLs keep working.
  const initialTab = searchParams.get("tab");
  const initialView = searchParams.get("view");
  const seedTab = TABS.some((t) => t.id === initialTab)
    ? initialTab
    : initialTab === "phonetic" ? "build" : "card";
  const seedView = initialTab === "phonetic" || initialView === "phonetic"
    ? "phonetic" : "visual";
  const [tab, setTab] = useState(seedTab);
  const [buildView, setBuildView] = useState(seedView);

  useEffect(() => {
    const wantedTab = searchParams.get("tab");
    const wantedView = searchParams.get("view");
    if (wantedTab === "phonetic") {
      // legacy shorthand → expand into (build, phonetic)
      setTab("build");
      setBuildView("phonetic");
    } else if (wantedTab && TABS.some((t) => t.id === wantedTab)) {
      setTab(wantedTab);
      if (wantedTab === "build" && wantedView) {
        setBuildView(wantedView === "phonetic" ? "phonetic" : "visual");
      }
    }
  }, [ch, searchParams]);

  const goTo = (next) => {
    if (next && next !== ch) navigate(`/kanji/${encodeURIComponent(next)}`);
  };
  const goToTab = (id) => {
    setTab(id);
    if (id === "card") {
      searchParams.delete("tab");
      searchParams.delete("view");
    } else {
      searchParams.set("tab", id);
      // When entering Build, remove any stale view param — defaults to visual.
      if (buildView !== "phonetic") searchParams.delete("view");
    }
    setSearchParams(searchParams, { replace: true });
  };
  const goToBuildView = (id) => {
    setBuildView(id);
    setTab("build");
    searchParams.set("tab", "build");
    if (id === "phonetic") {
      searchParams.set("view", "phonetic");
    } else {
      searchParams.delete("view");
    }
    setSearchParams(searchParams, { replace: true });
  };
  const jumpToPhonetic = () => goToBuildView("phonetic");

  return (
    <div data-testid={TEST_IDS.focusPage} className="flex flex-col">
      {/* sub-header with back + tabs */}
      <div className="flex items-center justify-between gap-2 px-3 md:px-8 py-2 border-b border-borderDefault bg-washi/95 backdrop-blur-sm sticky top-[52px] md:top-[72px] z-20">
        <button
          data-testid={TEST_IDS.focusBack}
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] font-sans text-sumi hover:text-shu transition-colors px-2 py-1 shrink-0"
        >
          <ArrowLeft size={14} /> back
        </button>

        <nav className="flex items-center border border-borderDefault overflow-x-auto">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={t.testId}
                onClick={() => goToTab(t.id)}
                className={`px-3 md:px-5 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] font-sans border-l first:border-l-0 border-borderDefault transition-colors whitespace-nowrap ${
                  active ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <p className="font-sans text-base md:text-lg font-serif text-sumi shrink-0">
          {ch}
        </p>
      </div>

      <AnimatePresence mode="wait">
        {tab === "card" && (
          <motion.section
            key={ch + "-card"}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.2 }}
            className="bg-washiPanel"
          >
            <KanjiCard kanji={ch} onSelectKanji={goTo} onJumpToPhonetic={jumpToPhonetic} />
          </motion.section>
        )}

        {tab === "build" && (
          <motion.section
            key={ch + "-build"}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.2 }}
          >
            {/* Inner toggle: Visual vs Phonetic — both answer "how is
                this kanji built?", one graphically, one by sound family. */}
            <div className="flex items-center justify-center gap-2 px-3 md:px-8 py-2 border-b border-borderDefault bg-washi/60">
              <span className="text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">view</span>
              <div className="inline-flex border border-borderDefault" data-testid="build-view-toggle">
                {BUILD_VIEWS.map((v) => {
                  const active = buildView === v.id;
                  return (
                    <button
                      key={v.id}
                      data-testid={v.testId}
                      onClick={() => goToBuildView(v.id)}
                      className={`px-3 py-1 text-[11px] uppercase tracking-[0.2em] font-sans border-l first:border-l-0 border-borderDefault transition-colors ${
                        active ? "bg-sumi text-washi" : "text-sumi hover:text-shu"
                      }`}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {buildView === "visual" && (
              <BuildTree
                ch={ch}
                onSelect={goTo}
                compoundsAbove={<CompoundList ch={ch} onSelectKanji={goTo} />}
              />
            )}
            {buildView === "phonetic" && (
              <PhoneticTree ch={ch} onSelect={goTo} />
            )}
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
}
